import { type FC, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { useHomePageContext } from '../HomePageContext';
import HpSharedVideoComponentTitle from './HpSharedVideoComponentTitle';
import HpSharedVideoComponentVideo from './HpSharedVideoComponentVideo';
import HpSharedVideoComponentCarousel from './HpSharedVideoComponentCarousel';
import { trackVideoModuleDisplay } from './videoComponentTrackingHelpers';

import { type HpSharedVideoComponent_user$key } from './__generated__/HpSharedVideoComponent_user.graphql';
import { type HpSharedVideoComponent_componentModule$key } from './__generated__/HpSharedVideoComponent_componentModule.graphql';

import styles from './HpSharedVideoComponent.scss';

type Props = {
    user?: HpSharedVideoComponent_user$key | null;
    fetchFavorites: boolean;
    componentModule: HpSharedVideoComponent_componentModule$key;
};

const userFragment = graphql`
    fragment HpSharedVideoComponent_user on User {
        ...HpSharedVideoComponentCarousel_user
    }
`;

const componentModuleFragment = graphql`
    fragment HpSharedVideoComponent_componentModule on VideoModule {
        userType
        videoId
        ...HpSharedVideoComponentTitle_componentModule
        ...HpSharedVideoComponentVideo_componentModule
        ...HpSharedVideoComponentCarousel_componentModule
    }
`;

const HpSharedVideoComponent: FC<Props> = ({
    user: userRef,
    componentModule: componentModuleRef,
    fetchFavorites,
}) => {
    const { isMobile, userType } = useHomePageContext();
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const user = useFragment(userFragment, userRef ?? null);
    const videoRef = useRef(null);

    const { userType: moduleUserType, videoId } = componentModule || {};
    const upperUserType = userType?.toUpperCase();
    const shouldShowForUserType =
        !moduleUserType || moduleUserType === 'ALL' || moduleUserType === upperUserType;

    if (!shouldShowForUserType || !videoId) {
        return null;
    }

    return (
        <>
            <VisibilityTracker
                elementRef={videoRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackVideoModuleDisplay();
                    }
                }}
            />
            <div
                ref={videoRef}
                className={classnames(
                    dibsCss.wScreen,
                    dibsCss.bgBuyertertiary,
                    styles.container,
                    dibsCss.pb0,
                    {
                        [dibsCss.pLarger]: !isMobile,
                        [dibsCss.pSmall]: isMobile,
                        [dibsCss.mtLarger]: !isMobile,
                        [dibsCss.mtSmall]: isMobile,
                    }
                )}
            >
                <div className={classnames(dibsCss.maxW1440px, dibsCss.my0, dibsCss.mxAuto)}>
                    {isMobile ? (
                        <div className={dibsCss.mtSmall}>
                            <HpSharedVideoComponentTitle componentModule={componentModule} />
                            <div className={dibsCss.mtMedium}>
                                <div className="primary-viewport-width">
                                    <HpSharedVideoComponentVideo
                                        componentModule={componentModule}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={dibsCss.flex}>
                            <HpSharedVideoComponentVideo componentModule={componentModule} />
                            <div className={classnames(dibsCss.mlXlarge, dibsCss.contentCenter)}>
                                <HpSharedVideoComponentTitle componentModule={componentModule} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div
                className={classnames(dibsCss.bgBuyertertiary, dibsCss.wScreen, styles.container, {
                    [dibsCss.pxLarge]: !isMobile,
                    [dibsCss.pyLarger]: !isMobile,
                    [dibsCss.pxSmall]: isMobile,
                    [dibsCss.pyMedium]: isMobile,
                })}
            >
                <div className={classnames(dibsCss.maxW1440px, dibsCss.my0, dibsCss.mxAuto)}>
                    <div className={isMobile ? 'primary-viewport-width' : undefined}>
                        <HpSharedVideoComponentCarousel
                            user={user}
                            fetchFavorites={fetchFavorites}
                            componentModule={componentModule}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default HpSharedVideoComponent;
